import { httpRequest } from '@/request';
import { armsErrorSend } from '@/utils/libs/armsErrorSend';
import { SERVER } from '@/request/config';
import { feishuRobotSend } from '@/utils/feishuRobot';
import { browserPushUrl } from '@/utils';
import { encodeURIEncryptFE } from '@/utils/libs/JSEncrypt';
import { message } from 'antd';
import { CountryCode } from '..';

let clientRef = null; // google实例
let initGooglePay = false; // 是否已经进行过初始化
let isGoolePayReady = false; // 是否可以使用googlePay

/** @description GooglePay支持的国家 */
const enabledCountry = [
  'AD',
  'AU',
  'AT',
  'BE',
  'BR',
  'BN',
  'BG',
  'CA',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HK',
  'HU',
  'IS',
  'IN',
  'IE',
  'IL',
  'IT',
  'JP',
  'KW',
  'LV',
  'LI',
  'LT',
  'LU',
  'MY',
  'MT',
  'MX',
  'MC',
  'NL',
  'NZ',
  'NO',
  'PL',
  'PT',
  'RO',
  'SA',
  'SG',
  'SK',
  'SI',
  'ES',
  'SE',
  'CH',
  'TW',
  'TH',
  'AE',
  'GB',
  'US', // 美国不上GP（临时）
  'VN',
];

// 收款通道 - 地区映射
const CKO_CHANNEL_ID_MAP = {
  '欧洲通道': {
    area: [
      'AD',
      'AT',
      'BE',
      'BG',
      'HR',
      'CY',
      'CZ',
      'DK',
      'EE',
      'FI',
      'FR',
      'DE',
      'GR',
      'HU',
      'IS',
      'IE',
      'IT',
      'KW',
      'LV',
      'LI',
      'LT',
      'LU',
      'MT',
      'MC',
      'NL',
      'NO',
      'PL',
      'PT',
      'RO',
      'SA',
      'SK',
      'SI',
      'ES',
      'SE',
      'CH',
      'TH',
      'AE',
      'GB',
    ],
    id: 1,
  },
  '美国通道': {
    area: ['US'],
    id: 2,
  },
  '香港通道': {
    area: ['AU', 'BR', 'BN', 'CA', 'HK', 'IN', 'IL', 'JP', 'MY', 'MX', 'NZ', 'SG', 'TW', 'VN'],
    id: 3,
  },
};

/** 根据当前订单会话地区分配通道，由业务进行确定 */
const getPaymentChannelId = (countryCode) => {
  for (const channelKey in CKO_CHANNEL_ID_MAP) {
    if (CKO_CHANNEL_ID_MAP.hasOwnProperty(channelKey)) {
      const channel = CKO_CHANNEL_ID_MAP[channelKey];
      if (channel.area.includes(countryCode ?? CountryCode)) {
        return channel.id;
      }
    }
  }
  feishuRobotSend({ text: `Google Pay 通道未配置 - ${countryCode ?? CountryCode}，默认走香港通道`, at: ['ccb'] });
  return CKO_CHANNEL_ID_MAP['香港通道'].id;
};

/** @description 根据收款通道的决定countryCode的值 */
const getGooglePayCountryCode = (countryCode) => {
  const GooglePayCountryCodeMap = {
    1: 'FR', // 欧洲通道对应的收单行code
    2: 'US', // 美国通道对应的收单行code
    3: 'HK', // 香港通道对应的收单行code
  };
  const channelId = getPaymentChannelId(countryCode);
  return GooglePayCountryCodeMap[channelId];
};

function getClient() {
  if (clientRef === null && window.google) {
    // clientRef = new window.google.payments.api.PaymentsClient({ environment: 'TEST' });
    clientRef = new window.google.payments.api.PaymentsClient({ environment: window.isProd ? 'PRODUCTION' : 'TEST' });
  }
  return clientRef;
}

/** @description 当前地区是否支持GP */
const getGooglePayEnable = (code) => {
  return enabledCountry.includes(code);
};

/** @description 向后端发起支付请求 */
const toGooglePay = ({ orderNumber, signature, protocolVersion, signedMessage, countryCode, payMode }) => {
  httpRequest({
    url: SERVER.API_SERVER_BASE + '/payment/checkout/ckoGooglePay',
    method: 'POST',
    data: {
      orderNumber,
      payMode,
      paymentChannelId: getPaymentChannelId(countryCode),
      gpInnerData: {
        signature,
        protocolVersion,
        signedMessage,
        forterTokenCookie: window.localStorage.getItem('ftr-token'),
      },
    },
  })
    .then((res) => {
      if (res.code === 0) {
        const { payUrl, orderNumber } = res.data;
        if (payUrl) {
          window.location.href = payUrl;
        } else {
          browserPushUrl(`/order/result/${encodeURIEncryptFE(orderNumber)}`);
        }
        // console.log('success', res);
      } else {
        // console.log('fail', res);
        armsErrorSend(res, {
          filename: 'Google Pay Exception',
          data: { msg: 'Google Pay 支付失败', errorMsg: res },
        });
        browserPushUrl(`/order/result/${encodeURIEncryptFE(orderNumber)}`);
      }
    })
    .catch((err) => {
      // console.log('fail', err);
      armsErrorSend(err, {
        filename: 'Google Pay Exception',
        data: { msg: 'Google Pay 支付失败', errorMsg: err },
      });
      browserPushUrl(`/order/result/${encodeURIEncryptFE(orderNumber)}`);
    });
};

/** @description  唤起GooglePay支付窗口，并向GooglePay获取支付token */
const wakeUpPaymentClient = (params) => {
  const paymentsClient = getClient();
  let {
    currencyCode,
    totalAmount = 0,
    countryCode,
    orderNumber,
    orderState,
    isOrderConfirmPage,
    LANG,
    balancePrice,
    shippingFee,
    couponFee,
    taxFee,
    payMode,
  } = params;

  try {
    // 日韩需要取整
    if (['JP', 'KR'].includes(countryCode)) {
      totalAmount = parseInt(totalAmount);
      balancePrice = parseInt(balancePrice);
      shippingFee = parseInt(shippingFee);
      couponFee = parseInt(couponFee);
      taxFee = parseInt(taxFee);
    }

    const paymentDataRequest = {
      apiVersion: 2,
      apiVersionMinor: 0,
      merchantInfo: {
        merchantName: 'Insta360 Store', // 商户名称
        merchantId: 'BCR2DN4T7XN5RGBC', // 商户ID
      },
      allowedPaymentMethods: [
        {
          type: 'CARD',
          parameters: {
            allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'], // 支持的授权方式
            allowedCardNetworks: ['VISA', 'MASTERCARD'], // 支持的卡种
          },
          tokenizationSpecification: {
            type: 'PAYMENT_GATEWAY',
            parameters: {
              gateway: 'checkoutltd',
              // gatewayMerchantId: 'pk_sbox_abw4pj3irjxhpzwnrozsluhh4uc', // PSP的key
              gatewayMerchantId: window.isProd
                ? 'pk_5t22vpmiyoszewqqq6dstbwnq4x'
                : 'pk_sbox_abw4pj3irjxhpzwnrozsluhh4uc', // 正式
            },
          },
        },
      ],
      transactionInfo: {
        displayItems: [
          {
            label: LANG['common.label.balancePrice'],
            type: 'SUBTOTAL',
            price: balancePrice?.toString(),
          },
          {
            label: LANG['common.label.shippingCost'],
            type: 'LINE_ITEM',
            price: shippingFee?.toString(),
          },
        ],
        countryCode: getGooglePayCountryCode(countryCode),
        // countryCode: 'FR',
        currencyCode: currencyCode,
        totalPriceStatus: 'FINAL',
        totalPrice: totalAmount?.toString(),
        checkoutOption: 'COMPLETE_IMMEDIATE_PURCHASE',
        totalPriceLabel: LANG['common.label.priceTotal'],
      },
    };

    // 从订单信息中获取额外订单支付数据
    const lineItemExtra = {
      couponFee: {
        label: LANG['common.label.couponCode'],
        type: 'LINE_ITEM',
        price: '-' + couponFee?.toString(),
      },
      taxFee: {
        label: LANG['common.label.balanceTax'],
        type: 'LINE_ITEM',
        price: taxFee?.toString(),
      },
    };

    // 如果有折扣
    if (couponFee > 0) paymentDataRequest.transactionInfo.displayItems.push(lineItemExtra.couponFee);
    // 如果有税费
    if (taxFee > 0) {
      // 不同地区展示不同税费文案
      switch (countryCode) {
        case 'GR':
          lineItemExtra.taxFee.label = 'Custom Service Fee';
          break;
        case 'CA':
          lineItemExtra.taxFee.label = 'Import Tax';
          break;
        case 'US':
          lineItemExtra.taxFee.label = 'Estimated Sales Tax';
          break;
        default:
      }
      paymentDataRequest.transactionInfo.displayItems.push(lineItemExtra.taxFee);
    }

    // console.log(paymentDataRequest);

    setTimeout(() => {
      paymentsClient
        .loadPaymentData(paymentDataRequest)
        .then(function (paymentData) {
          const { tokenizationData } = paymentData.paymentMethodData;
          const tokenInfo = JSON.parse(tokenizationData.token);
          // console.log(tokenizationData);
          // console.log(tokenizationData.token);

          toGooglePay({
            isOrderConfirmPage,
            LANG,
            orderState,
            orderNumber,
            countryCode,
            signature: tokenInfo.signature,
            protocolVersion: tokenInfo.protocolVersion,
            signedMessage: tokenInfo.signedMessage,
            payMode,
          });
        })
        .catch((err) => {
          // console.dir(err);
          switch (err.statusCode) {
            case 'CANCELED':
              if (isOrderConfirmPage) {
                // 结算页跳转到支付结果页的情况
                // 1. 超时
                // 2. 多窗口支付
                if (err.message === 'The "paymentrequest" event timed out after 5 minutes.') {
                  browserPushUrl(`/order/result/${encodeURIEncryptFE(orderNumber)}`);
                } else if (
                  err.message === 'Another PaymentRequest UI is already showing in a different tab or window.'
                ) {
                  message.info(LANG['payments.methods.googlepay_error_1']);
                  orderState.setStateValue('_beingPaid', false);
                  setTimeout(() => {
                    browserPushUrl(`/order/result/${encodeURIEncryptFE(orderNumber)}`);
                  }, 1000);
                } else {
                  browserPushUrl(`/order/pay/${encodeURIEncryptFE(orderNumber)}`);
                }
              } else {
                // 二次支付页多窗口支付异常则提示
                if (err.message === 'Another PaymentRequest UI is already showing in a different tab or window.') {
                  message.info(LANG['payments.methods.googlepay_error_1']);
                }

                orderState.setStateValue('_beingPaid', false);
                document.body.removeAttribute('data-HideCustomerService');
              }
              break;
            default:
              browserPushUrl(`/order/result/${encodeURIEncryptFE(orderNumber)}`);
              armsErrorSend(err, {
                filename: 'Google Pay Exception',
                data: { msg: 'loadPaymentData调用失败', errorMsg: err },
              });
              break;
          }
        });
    }, 1);
  } catch (err) {
    if (isOrderConfirmPage) browserPushUrl(`/order/pay/${encodeURIEncryptFE(orderNumber)}`);
    else {
      message.info(LANG['payments.methods.google_exception']);
      orderState.setStateValue('_beingPaid', false);
      document.body.removeAttribute('data-HideCustomerService');
    }
    armsErrorSend(err, {
      filename: 'Google Pay Exception',
      data: { msg: 'loadPaymentData调用失败', errorMsg: err },
    });
  }
};

/**
 * @description 确定用户当前环境是否支持GooglePay
 */
const isReadyToGooglePay = async (code, successCB) => {
  if (initGooglePay || !getGooglePayEnable(code)) return;
  initGooglePay = true;

  const maxCount = 10;
  let count = 1;
  const isReadyToPayRequest = {
    apiVersion: 2,
    apiVersionMinor: 0,
    allowedPaymentMethods: [
      {
        type: 'CARD',
        parameters: {
          allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
          allowedCardNetworks: ['VISA', 'MASTERCARD'],
        },
      },
    ],
  };
  try {
    let paymentsClient = getClient();
    // console.log('获取GooglePay客户端次数', count);
    let timer = null;
    const checkUserEnv = async (client, successCB) => {
      try {
        const response = await client.isReadyToPay(isReadyToPayRequest);
        if (response.result) {
          // console.log('支持 GooglePay');
          isGoolePayReady = true;
          successCB?.();
        } else {
          // console.log('不支持 GooglePay');
          armsErrorSend(response, {
            filename: 'Google Pay Exception',
            data: { msg: '用户环境不支持GooglePay', errorMsg: response },
          });
        }
      } catch (e) {
        armsErrorSend(e, {
          filename: 'Google Pay Exception',
          data: { msg: '获取GooglePay客户端失败', errorMsg: e },
        });
      }
    };

    const fetchClient = () => {
      paymentsClient = getClient();
      if (paymentsClient) {
        // console.log('获取GooglePay客户端成功');
        clearInterval(timer);
        checkUserEnv(paymentsClient, successCB);
      }
    };

    timer = setInterval(() => {
      count++;
      // console.log('获取GooglePay客户端次数', count);
      fetchClient();
      if (count > maxCount) {
        clearInterval(timer);
        armsErrorSend('获取GooglePay客户端次数超过10次', {
          filename: 'Google Pay Exception',
        });
      }
    }, 500);
  } catch (e) {
    armsErrorSend(e, {
      filename: 'Google Pay Exception',
      data: { msg: '获取GooglePay客户端失败', errorMsg: e },
    });
  }
};

export default {
  wakeUpPaymentClient,
  getGooglePayEnable,
  isReadyToGooglePay,
  get GoolePayReady() {
    return isGoolePayReady;
  },
};
